import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { Clock, Car, MapPin, Play } from 'lucide-react';

interface JourneyData {
  id: string;
  status: 'OPEN' | 'CLOSED';
  started_at: string;
  ended_at: string | null;
  has_vehicle: boolean;
  km_start: number | null;
  km_end: number | null;
  visit_count: number;
  total_km: number | null;
}

function formatElapsedTime(startedAt: string, endedAt?: string | null): string {
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const diff = Math.max(0, Math.floor((end - start) / 1000));
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export default function JourneyCard() {
  const [elapsed, setElapsed] = useState('00:00');

  const { data: journey } = useQuery<JourneyData | null>({
    queryKey: ['journey', 'today'],
    queryFn: () => api.get('/journeys/today').then(res => res.data.data).catch(() => null),
    refetchInterval: 60000,
  });

  useEffect(() => {
    if (!journey || journey.status !== 'OPEN') return;

    setElapsed(formatElapsedTime(journey.started_at));
    const timer = setInterval(() => {
      setElapsed(formatElapsedTime(journey.started_at));
    }, 60000);

    return () => clearInterval(timer);
  }, [journey]);

  // No journey yet
  if (!journey) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-gray-100">
            <Play className="h-5 w-5 text-gray-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Jornada de Trabalho</p>
            <p className="text-xs text-gray-400">Jornada inicia no primeiro check-in</p>
          </div>
        </div>
      </div>
    );
  }

  // Journey closed
  if (journey.status === 'CLOSED') {
    const totalTime = formatElapsedTime(journey.started_at, journey.ended_at);
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            <p className="text-sm font-semibold text-blue-900">Jornada Finalizada</p>
          </div>
          <span className="text-sm font-bold text-blue-700">{totalTime}</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-blue-700">
          <div className="flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" />
            <span>{journey.visit_count} visita{journey.visit_count !== 1 ? 's' : ''}</span>
          </div>
          {journey.has_vehicle && journey.total_km != null && (
            <div className="flex items-center gap-1">
              <Car className="h-3.5 w-3.5" />
              <span>{Number(journey.total_km).toFixed(1)} km</span>
            </div>
          )}
          <span>
            Inicio: {new Date(journey.started_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </div>
    );
  }

  // Journey open
  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Clock className="h-5 w-5 text-green-600" />
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-green-500 animate-pulse" />
          </div>
          <p className="text-sm font-semibold text-green-900">Jornada em Andamento</p>
        </div>
        <span className="text-lg font-bold text-green-700 tabular-nums">{elapsed}</span>
      </div>
      <div className="flex items-center gap-4 text-xs text-green-700">
        <div className="flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5" />
          <span>{journey.visit_count} visita{journey.visit_count !== 1 ? 's' : ''}</span>
        </div>
        {journey.has_vehicle && (
          <div className="flex items-center gap-1">
            <Car className="h-3.5 w-3.5" />
            <span>Veicular</span>
          </div>
        )}
        <span>
          Inicio: {new Date(journey.started_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}
